import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, CardTitle, cn } from '@insolech/ui';
import { Clock } from 'lucide-react';
import { useCardVisibilityFilter } from '../marketplace/shared/useCardVisibilityFilter';
import { visibleSubtypes } from './solutionSubtypeVisibility';
import { SUBTYPES, type SolutionSubtype } from './solutionSubtypes';

/**
 * Step 3 (custom branch) - Solution subtype picker.
 *
 * R28 - full product family rebuild per Rafael:
 *   - 7 boxes, ordered: PA12 / TPU / Polypropylene / Carbon fibre on row 1,
 *     EVA / Fibreglass / Orthopaedic sandals on row 2 (grid-cols-4 wraps).
 *   - Box content trimmed to: name, one-line grey description, delivery
 *     timeline. (Material spec line removed.)
 *   - Delivery timelines per Rafael: PA12 + TPU = 5 working days, the
 *     remaining insoles = 7-10, sandals = 10-14.
 *   - Each box can carry multiple photos. When >1, the photos crossfade
 *     in a 3-second loop (CSS opacity transition + setInterval).
 *
 * Real product photos drop into apps/web/public/products/. Until they're
 * uploaded the onError fallback shows the existing SVG silhouette.
 *
 * Root cause of the multi-hour deploy freeze: Vercel's "Ignored Build Step"
 * was set to "Only build pre-production", whose command was
 *   if [ "$VERCEL_ENV" == "preview" ]; then exit 1; else exit 0; fi
 * which silently skipped every production push to main. Switched to
 * "Automatic" and the queue resumed (2026-05-14).
 */

export { SUBTYPES } from './solutionSubtypes';
export type { SolutionSubtype, SubtypeOption } from './solutionSubtypes';

interface Props {
  value: SolutionSubtype | null;
  onChange: (next: SolutionSubtype) => void;
}

export function SolutionSubtypePicker({ value, onChange }: Props) {
  const { t } = useTranslation('orders');
  // Custom-made cards now respect admin card-visibility toggles too (a disabled
  // material drops out of the picker), while the current value stays visible so
  // existing orders still edit correctly.
  const { isHidden } = useCardVisibilityFilter();
  const subtypes = visibleSubtypes(SUBTYPES, value, isHidden);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('subtype_picker_title')}</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-sm text-text-secondary mb-4">{t('subtype_picker_subtitle')}</p>
        {/* R28 follow-up - tighter grid + smaller padding. Boxes are
            noticeably smaller; image uses object-contain so nothing is cut. */}
        <ul
          role="radiogroup"
          aria-label={t('subtype_picker_title')}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-2"
        >
          {subtypes.map((opt) => {
            const selected = value === opt.id;
            return (
              <li key={opt.id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={`subtype-${opt.id}`}
                  onClick={() => onChange(opt.id)}
                  className={`group w-full text-start p-2 rounded-lg border-2 transition-all bg-surface-elevated ${
                    selected
                      ? 'border-brand-500 ring-2 ring-brand-500/20 bg-brand-50'
                      : 'border-border-subtle hover:border-brand-300'
                  }`}
                >
                  {/* bg-white matches the product photos' own white background
                      so there's no visible edge around the image. */}
                  <div className="aspect-[4/3] w-full rounded-md bg-white overflow-hidden">
                    <ProductImageCarousel images={opt.images} fallback={opt.fallback} />
                  </div>
                  <div className="mt-1.5">
                    <h3 className="text-sm font-semibold text-text-primary leading-tight">
                      {t(opt.nameKey)}
                    </h3>
                    <p className="mt-0.5 text-xs text-text-muted line-clamp-2">
                      {t(opt.descKey)}
                    </p>
                    <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-text-secondary">
                      <Clock className="h-3 w-3" aria-hidden="true" />
                      {opt.delivery}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}

/**
 * ProductImageCarousel - horizontal slide carousel through `images`.
 *
 * Rafael's preference (matches the marketing site's testimonials track):
 * a sliding right-to-left transition instead of an opacity crossfade.
 *
 * Implementation: track is a flex row of N images, each 100% wide. The
 * track is translated left by index*100% with a 700ms transform
 * transition, advanced on a 3-second setInterval. After landing on the
 * last image, we ride one more "synthetic" tick into a cloned first
 * image then snap back without transition - giving an endless one-way
 * slide instead of the visually jarring "rewind to start".
 *
 * Images use object-contain so the whole insole / sandal is visible —
 * earlier object-cover was cropping the wider sandal photos.
 */
function ProductImageCarousel({
  images,
  fallback,
}: {
  images: string[];
  fallback: string;
}) {
  const [index, setIndex] = React.useState(0);
  // animateOff suppresses the transform transition for the snap-back tick
  // after we land on the clone of the first image, so the user never sees
  // a fast rewind animation.
  const [animateOff, setAnimateOff] = React.useState(false);

  React.useEffect(() => {
    if (images.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => i + 1);
    }, 3000);
    return () => window.clearInterval(id);
  }, [images.length]);

  // When index passes the last real image (we're showing the cloned
  // first), wait for the transition to finish then snap back to 0
  // without animation. This produces a smooth, infinite right-to-left
  // slide.
  React.useEffect(() => {
    if (images.length <= 1) return undefined;
    if (index !== images.length) return undefined;
    const timeout = window.setTimeout(() => {
      setAnimateOff(true);
      setIndex(0);
      // Re-enable animation on the next frame.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateOff(false));
      });
    }, 720);
    return () => window.clearTimeout(timeout);
  }, [index, images.length]);

  if (images.length === 0) {
    return <img src={fallback} alt="" className="h-full w-full object-contain p-2" />;
  }

  if (images.length === 1) {
    const only = images[0]!;
    return (
      <img
        src={only}
        alt=""
        loading="lazy"
        onError={(e) => {
          const img = e.currentTarget;
          if (img.src.endsWith(fallback)) return;
          img.src = fallback;
        }}
        className="h-full w-full object-contain p-2"
      />
    );
  }

  // Track contains the images plus a cloned first image at the end so the
  // wrap-around slide reads as continuous motion.
  const track = [...images, images[0]!];

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        className={cn(
          'flex h-full w-full ease-out',
          animateOff ? '' : 'transition-transform duration-700',
        )}
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {track.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className="relative h-full w-full shrink-0"
            aria-hidden={i !== index}
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.src.endsWith(fallback)) return;
                img.src = fallback;
              }}
              className="absolute inset-0 h-full w-full object-contain p-2"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
